import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { HomeContent, DEFAULT_HOME_CONTENT } from '../types';
import connerSeal from '../assets/images/conner-seal.jpg';
import municipalHall from '../assets/images/municipal-hall.jpg';

// One icon per hero step, in order. Kept parallel to hero.steps rather
// than stored with the copy: the Super Admin edits wording, not artwork,
// and a step beyond the fourth simply renders without an icon.
const STEP_ICONS = [
  // Document
  'M6 2h7l5 5v15H6zM13 2v5h5',
  // Check in a circle
  'M12 2a10 10 0 100 20 10 10 0 000-20zM8 12l3 3 5-5',
  // Magnifier
  'M11 4a7 7 0 100 14 7 7 0 000-14zM16 16l5 5',
  // Refresh
  'M20 12a8 8 0 11-2.3-5.7M20 4v4h-4'
];

const HIGHLIGHT_ICONS = [
  // Padlock
  'M6 10h12v10H6zM9 10V7a3 3 0 016 0v3',
  // Clock
  'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2',
  // Chat bubble
  'M21 12a8 8 0 01-11.6 7.1L3 21l1.9-6.4A8 8 0 1121 12z'
];

function Icon({ path, size = 18 }: { path: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

const SECTION_IDS = {
  about: 'about',
  programs: 'programs',
  guidelines: 'guidelines',
  contact: 'contact'
};

const HomePage = () => {
  // Starts on the defaults rather than a spinner: this is the first page
  // a visitor sees, and a blank frame while the copy loads is worse than
  // briefly showing the built-in wording.
  const [content, setContent] = useState<HomeContent>(DEFAULT_HOME_CONTENT);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getHomeContent()
      .then((loaded) => {
        if (!cancelled) setContent(loaded);
      })
      .catch(() => {
        // Non-fatal — the page stays on DEFAULT_HOME_CONTENT. The public
        // landing page must render even when the API is asleep (Render's
        // free instance spins down), since that's exactly when a first-
        // time visitor is most likely to arrive.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { brand, nav, hero, about, programs, guidelines, contact, footer } = content;

  const navLinks = [
    { label: nav.about, href: `#${SECTION_IDS.about}` },
    { label: nav.programs, href: `#${SECTION_IDS.programs}` },
    { label: nav.guidelines, href: `#${SECTION_IDS.guidelines}` },
    { label: nav.contact, href: `#${SECTION_IDS.contact}` }
  ];

  const section = (id: string, data: { title: string; intro: string; cards: { title: string; body: string }[] }) => (
    <section className="home-section" id={id}>
      <div className="home-container">
        <h2 className="home-section-title">{data.title}</h2>
        {data.intro && <p className="home-section-intro">{data.intro}</p>}
        <div className="home-card-grid">
          {data.cards.map((card, i) => (
            <article className="home-card" key={`${id}-${i}`}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="home-page">
      <header className="home-header">
        <div className="home-container home-header-inner">
          <a className="home-brand" href="#top">
            <img src={connerSeal} alt="Municipality of Conner, Apayao seal" width={56} height={56} />
            <span>
              <strong>{brand.acronym}</strong>
              <small>{brand.fullName}</small>
            </span>
          </a>

          <button
            className="home-nav-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>

          <nav className={`home-nav${menuOpen ? ' open' : ''}`}>
            <a className="home-nav-link active" href="#top" onClick={() => setMenuOpen(false)}>
              {nav.home}
            </a>
            {navLinks.map((link, i) => (
              <a className="home-nav-link" key={`nav-${i}`} href={link.href} onClick={() => setMenuOpen(false)}>
                {link.label}
              </a>
            ))}
            <Link className="home-btn home-btn-outline" to="/login" onClick={() => setMenuOpen(false)}>
              {nav.signIn}
            </Link>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="home-hero">
          <div className="home-hero-media" aria-hidden="true">
            <img src={municipalHall} alt="" />
          </div>

          <div className="home-container home-hero-inner">
            <div className="home-hero-copy">
              <h1>
                {hero.titleLead} <span className="home-accent">{hero.titleAccent}</span> {hero.titleRest}
              </h1>

              <ul className="home-steps">
                {hero.steps.map((step, i) => (
                  <li key={`step-${i}`}>
                    <span className="home-step-icon">
                      <Icon path={STEP_ICONS[i % STEP_ICONS.length]} size={16} />
                    </span>
                    {step}
                  </li>
                ))}
              </ul>

              <p className="home-hero-description">{hero.description}</p>

              <div className="home-hero-actions">
                <Link className="home-btn home-btn-primary" to="/register">
                  {hero.primaryCta} <span aria-hidden="true">&rarr;</span>
                </Link>
                <Link className="home-btn home-btn-outline" to="/login">
                  {hero.secondaryCta}
                </Link>
              </div>

              <div className="home-highlights">
                {hero.highlights.map((item, i) => (
                  <div className="home-highlight" key={`highlight-${i}`}>
                    <span className="home-highlight-icon">
                      <Icon path={HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length]} size={20} />
                    </span>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.body}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {section(SECTION_IDS.about, about)}
        {section(SECTION_IDS.programs, programs)}
        {section(SECTION_IDS.guidelines, guidelines)}

        <section className="home-section home-section-tint" id={SECTION_IDS.contact}>
          <div className="home-container">
            <h2 className="home-section-title">{contact.title}</h2>
            {contact.intro && <p className="home-section-intro">{contact.intro}</p>}
            <dl className="home-contact-list">
              {contact.details.map((detail, i) => (
                <div className="home-contact-row" key={`contact-${i}`}>
                  <dt>{detail.label}</dt>
                  <dd>{detail.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <footer className="home-footer">
        <div className="home-container">
          <p>{footer.note}</p>
          {/* The permanent home for these. The notice banner is
              dismissed once and never seen again, so it cannot be the
              only route to them. */}
          <p style={{ fontSize: '0.85rem' }}>
            <Link to="/about">About Us</Link> &middot; <Link to="/privacy">Data Privacy Policy</Link> &middot;{' '}
            <Link to="/terms">Terms and Conditions</Link>
          </p>
          <p className="home-footer-brand">{brand.fullName}</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
